import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { LuArrowLeftRight, LuBuilding2, LuCirclePlus, LuLoaderCircle, LuPencil, LuSave, LuToggleRight } from 'react-icons/lu';
import { Field, TextInput } from '../../../components/form/FormControls';
import Button from '../../../components/ui/Button';
import GlassIcon from '../../../components/ui/GlassIcon';
import Modal from '../../../components/ui/Modal';
import useBankAccountText from './bankAccountText';
import { EMPTY_FORM, FORM_FIELDS } from './bankAccountColumns';

const FORM_ID = 'bank-account-form';

// Formun bölümleri (eski formdaki alan sırası)
const SECTIONS = [
  { key: 'company', icon: LuBuilding2, fields: ['firma', 'sube', 'hesapNo', 'dosyaAciklama'] },
  { key: 'payment', icon: LuArrowLeftRight, fields: ['odemeKesinti', 'odemeTuru', 'odemeAciklama', 'odemeDonemi'] },
  { key: 'status', icon: LuToggleRight, fields: ['durum'] },
];

// Serbest metin alanları için öneriler (başka değer de yazılabilir)
const SUGGESTIONS = {
  odemeKesinti: ['Ödeme', 'Kesinti'],
  durum: ['Aktif', 'Pasif', 'Beklemede'],
};

const toForm = (account) => Object.fromEntries(FORM_FIELDS.map((key) => [key, account?.[key] == null ? '' : String(account[key])]));

/**
 * Add or edit a bank account (both were separate forms on the old page).
 * Every field is required, as before.
 */
const BankAccountFormModal = ({ isOpen, account, onClose, onSave }) => {
  const { language, label } = useBankAccountText();
  const isEditing = Boolean(account);
  const [formData, setFormData] = useState(() => (account ? toForm(account) : { ...EMPTY_FORM }));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const handleChange = (name, value) => {
    setFormData((current) => ({ ...current, [name]: value }));
    // Clear error for this field when typing
    if (errors[name]) setErrors((current) => ({ ...current, [name]: null }));
  };

  const validateForm = () => {
    const newErrors = {};
    FORM_FIELDS.forEach((key) => {
      if (!formData[key].trim()) newErrors[key] = label('form.required');
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) {
      toast.error(label('toast.fillAll'));
      return;
    }
    setSaving(true);
    const ok = await onSave(formData, account?.id || null);
    setSaving(false);
    if (ok) onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="form"
      dismissible={!saving}
      title={isEditing ? label('form.editTitle') : label('form.newTitle')}
      subtitle={isEditing ? account.firma : label('form.subtitle')}
      closeLabel={label('form.cancel')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-sky-400 tw-to-blue-600 tw-shadow-blue-500/30" size="xs">
          {isEditing ? <LuPencil aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" /> : <LuCirclePlus aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />}
        </GlassIcon>
      )}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>{label('form.cancel')}</Button>
          <Button variant="primary" type="submit" form={FORM_ID} disabled={saving}>
            {saving ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" /> : <LuSave aria-hidden="true" className="tw-h-4 tw-w-4" />}
            {saving ? label('form.saving') : isEditing ? label('form.update') : label('form.save')}
          </Button>
        </>
      )}
    >
      <form id={FORM_ID} onSubmit={handleSubmit} noValidate className="tw-space-y-5">
        {SECTIONS.map(({ key, icon: Icon, fields }) => (
          <fieldset key={key} className="tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-white/60 tw-p-4 dark:tw-border-white/10 dark:tw-bg-white/[0.02]">
            <legend className="tw-flex tw-items-center tw-gap-2 tw-px-1.5 tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">
              <Icon aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-blue-500" />
              {label(`form.sections.${key}`)}
            </legend>
            <div className="tw-grid tw-gap-3 sm:tw-grid-cols-2">
              {fields.map((name) => (
                <Field
                  key={name}
                  label={label(`fields.${name}`)}
                  htmlFor={`bank-${name}`}
                  required
                  error={errors[name] || undefined}
                  hint={name === 'odemeAciklama' ? label('form.odemeAciklamaHint') : undefined}
                  className={name === 'hesapNo' ? 'sm:tw-col-span-2' : ''}
                >
                  <TextInput
                    id={`bank-${name}`}
                    value={formData[name]}
                    onChange={(event) => handleChange(name, event.target.value)}
                    placeholder={label('form.placeholder', { field: label(`fields.${name}`) })}
                    list={SUGGESTIONS[name] ? `bank-${name}-options` : undefined}
                    error={errors[name] || undefined}
                    className={name === 'hesapNo' ? 'tw-font-mono tw-tracking-wide' : ''}
                  />
                  {SUGGESTIONS[name] && (
                    <datalist id={`bank-${name}-options`}>
                      {SUGGESTIONS[name].map((option) => <option key={option} value={option} />)}
                    </datalist>
                  )}
                </Field>
              ))}
            </div>
          </fieldset>
        ))}
      </form>
    </Modal>
  );
};

BankAccountFormModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  account: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default BankAccountFormModal;
